import _ from 'lodash'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'

import { WaitingRoom } from '@/core/models/waiting-room.model'
import { useGetConsultationQuery } from '@/core/services/consultation'
import useInterval from '@/hooks/useInterval'
import { countAvailableDoctors, toDoctorIds, useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'
import { getMinutesFromTimeString } from '@/utils/helpers'

/**
 * Whether a patient can be queued in the waiting room right now: the room must be open and, when the
 * queue would outlast the closing hour, at least one member doctor must be live-available.
 */
export function useCanReserveConsultation(waitingRoom?: WaitingRoom): boolean {
  const [currentTime, setCurrentTime] = useState(dayjs())

  useInterval(() => {
    setCurrentTime(dayjs())
  }, 60 * 1000)

  const { data: lastQueuedConsultation } = useGetConsultationQuery(_.last(waitingRoom?.consultations)?._id ?? skipToken)
  const { statuses: doctorStatuses, isReady: doctorStatusesReady } = useRoomDoctorStatuses(waitingRoom?._id)

  const hasAvailableDoctor = useMemo(
    () => doctorStatusesReady && countAvailableDoctors(toDoctorIds(waitingRoom?.doctors), doctorStatuses) > 0,
    [doctorStatuses, doctorStatusesReady, waitingRoom]
  )

  return useMemo(() => {
    if (!waitingRoom) return false

    const currentOpeningHour = _.find(waitingRoom.openingHours, ({ day }) => day === currentTime.day())
    const currentPeriod = _.find(currentOpeningHour?.periods, (period) => {
      const currentTimeInMinutes = currentTime.get('hours') * 60 + currentTime.get('minutes')
      return (
        currentTimeInMinutes >= dayjs.duration(getMinutesFromTimeString(period.open), 'minutes').asMinutes() &&
        currentTimeInMinutes <= dayjs.duration(getMinutesFromTimeString(period.close), 'minutes').asMinutes()
      )
    })

    if (!currentPeriod) return false

    if (!lastQueuedConsultation)
      return currentTime.add(waitingRoom.avgWaitingTime, 'minutes').isSameOrAfter(dayjs(currentPeriod.close, 'HH:mm'))
        ? hasAvailableDoctor
        : true

    return dayjs(lastQueuedConsultation.startDate)
      .add(waitingRoom.avgWaitingTime * 2, 'minutes')
      .isSameOrAfter(dayjs(currentPeriod.close, 'HH:mm'))
      ? hasAvailableDoctor
      : true
  }, [currentTime, hasAvailableDoctor, lastQueuedConsultation, waitingRoom])
}
